import { useState, useEffect } from 'react';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  Card, Button, Input, Select, InputNumber, Alert, Tag, Modal, Tabs, Form,
  Row, Col, Typography, Space, Radio, DatePicker, Upload, Empty, App as AntApp,
  Flex, Popconfirm, Checkbox,
} from 'antd';
import {
  PlusOutlined, SearchOutlined, DeleteOutlined, DownloadOutlined,
  UploadOutlined, FileTextOutlined, HistoryOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { API_URL } from '../config';
import { PROPS_DINHEIRO_BR } from '../utils/dinheiroInput';
import {
  tipoDoLancamento, ehEstorno, valorParaGravar, dinheiroComSinal,
} from '../utils/lancamentoManualSinal';
import { ehCompetenciaManual, normalizarCompetencia } from '../utils/periodoDre';

const { Title, Text } = Typography;

async function getToken(): Promise<string> {
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString() ?? '';
}

const CATEGORIAS_RECEITA = [
  { label: 'Receita Bruta — Topografia', grupo: 'RECEITA_BRUTA', area: 'Topografia' },
  { label: 'Receita Bruta — Consultoria Ambiental', grupo: 'RECEITA_BRUTA', area: 'Consultoria Ambiental' },
  { label: 'Receita Bruta — Projeto', grupo: 'RECEITA_BRUTA', area: 'Projeto' },
  { label: 'Receita Bruta — Hidrometria', grupo: 'RECEITA_BRUTA', area: 'Hidrometria' },
  { label: 'Receita Bruta — Fiscalização de Projeto', grupo: 'RECEITA_BRUTA', area: 'Fiscalização de Projeto' },
  { label: 'Receita Bruta — Segurança de Barragens', grupo: 'RECEITA_BRUTA', area: 'Segurança de Barragens' },
  { label: 'Outras Receitas Operacionais', grupo: 'OUTRAS_RECEITAS_OPERACIONAIS', area: '' },
  { label: 'Receitas Financeiras', grupo: 'RECEITAS_FINANCEIRAS', area: '' },
];

const CATEGORIAS_DESPESA = [
  { label: 'Comissões de Vendedores', grupo: 'COMISSOES' },
  { label: 'Alimentação em campo', grupo: 'CUSTOS_OPERACIONAIS' },
  { label: 'Aluguel de equipamentos', grupo: 'CUSTOS_OPERACIONAIS' },
  { label: 'Combustíveis', grupo: 'CUSTOS_OPERACIONAIS' },
  { label: 'Diárias / Hospedagem', grupo: 'CUSTOS_OPERACIONAIS' },
  { label: 'Impressões e Plotagens', grupo: 'CUSTOS_OPERACIONAIS' },
  { label: 'IPVA / DPVAT / Licenciamento', grupo: 'CUSTOS_OPERACIONAIS' },
  { label: 'Leasing - Veículos', grupo: 'CUSTOS_OPERACIONAIS' },
  { label: 'Manutenção de Equipamentos', grupo: 'CUSTOS_OPERACIONAIS' },
  { label: 'Manutenção de Veículos', grupo: 'CUSTOS_OPERACIONAIS' },
  { label: 'Materiais Aplicados na Prestação de Serviços', grupo: 'CUSTOS_OPERACIONAIS' },
  { label: 'Multas de Trânsito', grupo: 'CUSTOS_OPERACIONAIS' },
  { label: 'Passagens Aéreas / Ônibus', grupo: 'CUSTOS_OPERACIONAIS' },
  { label: 'Pedágios', grupo: 'CUSTOS_OPERACIONAIS' },
  { label: 'Seguro de Equipamentos', grupo: 'CUSTOS_OPERACIONAIS' },
  { label: 'Seguros de Veículos', grupo: 'CUSTOS_OPERACIONAIS' },
  { label: 'Software / Licença de Uso', grupo: 'CUSTOS_OPERACIONAIS' },
  { label: 'Transporte Urbano (táxi, Uber)', grupo: 'CUSTOS_OPERACIONAIS' },
  { label: 'ARTs', grupo: 'CUSTOS_OPERACIONAIS' },
  { label: 'Remuneração de Auxiliares e Freelancers', grupo: 'CUSTOS_OPERACIONAIS' },
  { label: 'Honorários Terceirizados', grupo: 'CUSTOS_OPERACIONAIS' },
  { label: 'Remuneração de Estagiários', grupo: 'CUSTOS_OPERACIONAIS' },
  { label: 'Remuneração CLT', grupo: 'CUSTOS_OPERACIONAIS' },
  { label: 'Remuneração de Autônomos', grupo: 'CUSTOS_OPERACIONAIS' },
  { label: 'Remuneração de Sócios', grupo: 'CUSTOS_OPERACIONAIS' },
  { label: 'Lanches e Refeições', grupo: 'DESPESAS_COMERCIAIS' },
  { label: 'Serviços / assinaturas comerciais', grupo: 'DESPESAS_COMERCIAIS' },
  { label: 'Marketing e Publicidade', grupo: 'DESPESAS_COMERCIAIS' },
  { label: 'Remuneração de Autônomos - Comercial', grupo: 'DESPESAS_COMERCIAIS' },
  { label: 'Aluguel', grupo: 'DESPESAS_ADMINISTRATIVAS' },
  { label: 'Combustíveis Diretoria', grupo: 'DESPESAS_ADMINISTRATIVAS' },
  { label: 'Bens de Pequeno Valor', grupo: 'DESPESAS_ADMINISTRATIVAS' },
  { label: 'Cartório', grupo: 'DESPESAS_ADMINISTRATIVAS' },
  { label: 'Confraternizações', grupo: 'DESPESAS_ADMINISTRATIVAS' },
  { label: 'Copa, Cozinha, Higiene e Limpeza', grupo: 'DESPESAS_ADMINISTRATIVAS' },
  { label: 'Energia Elétrica', grupo: 'DESPESAS_ADMINISTRATIVAS' },
  { label: 'Estacionamento', grupo: 'DESPESAS_ADMINISTRATIVAS' },
  { label: 'Exames Médicos', grupo: 'DESPESAS_ADMINISTRATIVAS' },
  { label: 'Ferramentas / EPIs', grupo: 'DESPESAS_ADMINISTRATIVAS' },
  { label: 'Honorários Contábeis', grupo: 'DESPESAS_ADMINISTRATIVAS' },
  { label: 'IPTU', grupo: 'DESPESAS_ADMINISTRATIVAS' },
  { label: 'Manutenção Predial', grupo: 'DESPESAS_ADMINISTRATIVAS' },
  { label: 'Materiais de Escritório', grupo: 'DESPESAS_ADMINISTRATIVAS' },
  { label: 'Medicina do Trabalho', grupo: 'DESPESAS_ADMINISTRATIVAS' },
  { label: 'Outras Despesas Administrativas', grupo: 'DESPESAS_ADMINISTRATIVAS' },
  { label: 'Plano de Saúde Sócios', grupo: 'DESPESAS_ADMINISTRATIVAS' },
  { label: 'Seguro de Imóveis', grupo: 'DESPESAS_ADMINISTRATIVAS' },
  { label: 'Serviços / Assinaturas', grupo: 'DESPESAS_ADMINISTRATIVAS' },
  { label: 'Telefonia e Internet', grupo: 'DESPESAS_ADMINISTRATIVAS' },
  { label: 'Tarifas Bancárias', grupo: 'DESPESAS_ADMINISTRATIVAS' },
  { label: 'Taxas', grupo: 'DESPESAS_ADMINISTRATIVAS' },
  { label: 'Uniformes', grupo: 'DESPESAS_ADMINISTRATIVAS' },
  { label: 'Mão de obra administrativa', grupo: 'DESPESAS_ADMINISTRATIVAS' },
  { label: 'Correios', grupo: 'DESPESAS_ADMINISTRATIVAS' },
  { label: 'Contribuição Sindical', grupo: 'OUTRAS_DESPESAS_OPERACIONAIS' },
  { label: 'PLR - Participação nos Lucros e Resultados', grupo: 'OUTRAS_DESPESAS_OPERACIONAIS' },
  { label: 'Cursos e Treinamentos', grupo: 'OUTRAS_DESPESAS_OPERACIONAIS' },
  { label: 'Despesas a Identificar', grupo: 'OUTRAS_DESPESAS_OPERACIONAIS' },
  { label: 'INSS sobre Pró-labore - GPS', grupo: 'OUTRAS_DESPESAS_OPERACIONAIS' },
  { label: 'INSS sobre Salários - GPS', grupo: 'OUTRAS_DESPESAS_OPERACIONAIS' },
  { label: 'INSS sobre Serviços de Terceiros (Autônomos) - DARF', grupo: 'OUTRAS_DESPESAS_OPERACIONAIS' },
  { label: 'FGTS e Multa de FGTS', grupo: 'OUTRAS_DESPESAS_OPERACIONAIS' },
  { label: 'Quebra de Caixa', grupo: 'OUTRAS_DESPESAS_OPERACIONAIS' },
  { label: 'Seguro de Vida', grupo: 'OUTRAS_DESPESAS_OPERACIONAIS' },
  { label: 'IOF', grupo: 'DESPESAS_FINANCEIRAS' },
  { label: 'Empréstimos e Dívidas', grupo: 'DESPESAS_FINANCEIRAS' },
  { label: 'Depreciação', grupo: 'DESPESAS_FINANCEIRAS' },
  { label: 'Amortização', grupo: 'DESPESAS_FINANCEIRAS' },
  { label: 'Outras Despesas Financeiras', grupo: 'DESPESAS_FINANCEIRAS' },
  { label: 'ISS sobre Faturamento', grupo: 'IMPOSTOS' },
  { label: 'IRPJ', grupo: 'IMPOSTOS' },
  { label: 'Outros Impostos (CSLL, PIS/COFINS, ETC.)', grupo: 'IMPOSTOS' },
  { label: 'Computadores e Periféricos', grupo: 'INVESTIMENTOS' },
  { label: 'Máquinas, Equipamentos, Utensílios e Ferramentas', grupo: 'INVESTIMENTOS' },
  { label: 'Veículos', grupo: 'INVESTIMENTOS' },
  { label: 'Outras Imobilizações por Aquisição', grupo: 'INVESTIMENTOS' },
  { label: 'Móveis, Utensílios e Instalações Administrativos', grupo: 'INVESTIMENTOS' },
];

interface Lancamento {
  id: string;
  periodo: string;
  descricao: string;
  categoria_dre: string;
  grupo_dre: string;
  valor: number;
  area_negocio: string;
  iss: number;
  ir: number;
  pis: number;
  csll: number;
  cofins: number;
  criado_em: string;
  fornecedor_cliente?: string | null;
  cnpj_cpf?: string | null;
  data_emissao?: string | null;
  data_vencimento?: string | null;
  data_pagamento?: string | null;
  status?: string;
  meio_pagamento?: string | null;
  nota_fiscal_numero?: string | null;
  observacoes?: string | null;
  atualizado_em?: string;
  atualizado_por?: string;
  // 🚨 A base do ISS retido sai DAQUI, não de `fornecedor_cliente` (texto livre):
  // o motor junta `comercial.clientes c ON c.id = lm.cliente_id` e só olha
  // `c.retentor_iss` (dre-engine/index.js). Sem cliente_id, o estorno devolve
  // PIS/COFINS/CSLL/IRPJ e NÃO devolve o ISS retido.
  cliente_id?: number | null;
}

const STATUS_TAG: Record<string, { color: string; label: string }> = {
  pago: { color: 'green', label: 'Pago/Recebido' },
  em_aberto: { color: 'gold', label: 'Em aberto' },
  cancelado: { color: 'default', label: 'Cancelado' },
};

function LancamentosManuaisInner() {
  const { message } = AntApp.useApp();
  const [tipo, setTipo] = useState<'receita' | 'despesa'>('despesa');
  const [periodo, setPeriodo] = useState('2026/01');
  const [descricao, setDescricao] = useState('');
  const [categoriaSel, setCategoriaSel] = useState('');
  const [valor, setValor] = useState<number>(0);
  const [estorno, setEstorno] = useState(false);
  const [clienteId, setClienteId] = useState<string>('');
  const [clientes, setClientes] = useState<Array<{ id: number; razao_social: string; retentor_iss: boolean }>>([]);
  const [salvando, setSalvando] = useState(false);
  const [lancamentos, setLancamentos] = useState<Lancamento[]>([]);
  const [filtroPeriodo, setFiltroPeriodo] = useState('2026');
  const [filtroStatus, setFiltroStatus] = useState<string>('');
  const [busca, setBusca] = useState('');
  const [editando, setEditando] = useState<Lancamento | null>(null);

  useEffect(() => { carregarLancamentos(); /* eslint-disable-next-line */ }, [filtroPeriodo]);
  useEffect(() => { carregarClientes(); }, []);

  async function carregarClientes() {
    try {
      const token = await getToken();
      const res = await axios.get(`${API_URL}/comercial/clientes`, { headers: { Authorization: token } });
      const data = res.data.body ? JSON.parse(res.data.body) : res.data;
      setClientes(Array.isArray(data) ? data : []);
    } catch {}
  }

  async function carregarLancamentos() {
    try {
      const token = await getToken();
      const res = await axios.get(`${API_URL}/manuais?trimestre=${encodeURIComponent(filtroPeriodo)}`, {
        headers: { Authorization: token },
      });
      const data = res.data.body ? JSON.parse(res.data.body) : res.data;
      setLancamentos(Array.isArray(data) ? data : []);
    } catch {}
  }

  async function salvar() {
    if (!periodo || !descricao || !categoriaSel || !valor) {
      message.warning('Preencha todos os campos obrigatórios.');
      return;
    }
    // 🚨 A competência é o ÚNICO campo que decide se o lançamento existe para
    // a DRE, e o motor casa por igualdade exata ('AAAA/MM'). A listagem casa
    // por `LIKE 'AAAA/%'`, então '2025/1' aparecia na tela — com tag, valor e
    // 'salvo com sucesso' — e não entrava em DRE nenhuma. Num estorno de
    // competência isso é R$ 496.100 de silêncio. Régua: utils/periodoDre.
    if (!ehCompetenciaManual(periodo)) {
      const sugestao = normalizarCompetencia(periodo);
      message.warning(`Período precisa ser AAAA/MM — ex: ${ehCompetenciaManual(sugestao) ? sugestao : '2026/01'}.`);
      return;
    }
    setSalvando(true);
    try {
      const token = await getToken();
      const cats = tipo === 'receita' ? CATEGORIAS_RECEITA : CATEGORIAS_DESPESA;
      const cat = cats.find((c: any) => c.label === categoriaSel) as any;
      const valorNum = valorParaGravar(valor, tipo, estorno);

      await axios.post(`${API_URL}/manuais`, {
        periodo, descricao, categoria_dre: categoriaSel,
        grupo_dre: cat?.grupo || '',
        valor: valorNum,
        area_negocio: cat?.area || '',
        cliente_id: clienteId ? parseInt(clienteId) : null,
        criado_por: 'usuario',
      }, { headers: { Authorization: token } });

      message.success('Lançamento salvo com sucesso.');
      setDescricao(''); setValor(0); setCategoriaSel(''); setClienteId(''); setEstorno(false);
      await carregarLancamentos();
    } catch {
      message.error('Erro ao salvar lançamento.');
    } finally { setSalvando(false); }
  }

  async function excluir(id: string) {
    try {
      const token = await getToken();
      await axios.delete(`${API_URL}/manuais/${id}`, { headers: { Authorization: token } });
      message.success('Lançamento excluído.');
      await carregarLancamentos();
    } catch {}
  }

  const fmt = (v: number) => 'R$ ' + Math.abs(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 });
  const cats = tipo === 'receita' ? CATEGORIAS_RECEITA : CATEGORIAS_DESPESA;

  const filtrados = lancamentos.filter(l => {
    if (filtroStatus && (l.status || 'pago') !== filtroStatus) return false;
    if (busca) {
      const q = busca.toLowerCase();
      return (l.descricao || '').toLowerCase().includes(q)
          || (l.fornecedor_cliente || '').toLowerCase().includes(q)
          || (l.nota_fiscal_numero || '').toLowerCase().includes(q)
          || (l.categoria_dre || '').toLowerCase().includes(q);
    }
    return true;
  });

  return (
    <div style={{ maxWidth: 820, margin: '16px auto', padding: '0 24px' }}>
      <Title level={4} style={{ margin: 0, color: '#020D7F' }}>Lançamentos manuais</Title>
      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 16 }}>
        Registre receitas e despesas que não estão no Conta Azul ou Notion.
      </Text>

      <Card size="small" style={{ marginBottom: 18 }}>
        <Radio.Group value={tipo} onChange={e => { setTipo(e.target.value); setCategoriaSel(''); }}
          optionType="button" buttonStyle="solid" style={{ marginBottom: 14 }}>
          <Radio.Button value="despesa">Despesa</Radio.Button>
          <Radio.Button value="receita">Receita</Radio.Button>
        </Radio.Group>

        <Form layout="vertical" size="small">
          <Row gutter={12}>
            <Col span={12}>
              {/* O onBlur conserta o jeito torto de escrever a MESMA competência
                  ('2025/1', '2025-12'); o que não dá pra consertar fica vermelho
                  aqui e o salvar() recusa. */}
              <Form.Item label="Período (AAAA/MM)" required
                validateStatus={periodo && !ehCompetenciaManual(periodo) ? 'error' : undefined}
                help={periodo && !ehCompetenciaManual(periodo)
                  ? 'Mês com dois dígitos e barra — a DRE só enxerga AAAA/MM.' : undefined}>
                <Input value={periodo} onChange={e => setPeriodo(e.target.value)}
                  onBlur={e => setPeriodo(normalizarCompetencia(e.target.value))}
                  placeholder="Ex: 2026/01" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Valor (R$)" required>
                <InputNumber {...PROPS_DINHEIRO_BR} value={valor} onChange={(v) => setValor(Number(v) || 0)}
                  min={0} step={0.01} style={{ width: '100%' }} placeholder="Ex: 15000" />
                <Checkbox checked={estorno} onChange={e => setEstorno(e.target.checked)} style={{ marginTop: 6 }}>
                  <Text style={{ fontSize: 12 }}>É um estorno — <strong>tira</strong> do período em vez de somar</Text>
                </Checkbox>
              </Form.Item>
            </Col>
          </Row>

          {estorno && (
            <Alert
              type="warning" showIcon
              message={`Vai ser gravado como ${dinheiroComSinal({ grupo_dre: tipo === 'receita' ? 'RECEITA_BRUTA' : 'CUSTOS_OPERACIONAIS', valor: valorParaGravar(valor, tipo, true) })}`}
              description={<Text style={{ fontSize: 11 }}>
                Use quando o valor já foi reconhecido em OUTRO período e precisa sair deste — por exemplo,
                adiantamento faturado num ano e executado no seguinte. Lance o positivo no período que
                recebe e o estorno no período que devolve, <strong>sempre na mesma categoria e no
                mesmo cliente</strong>, senão a DRE fica com a receita contada duas vezes.
                {tipo === 'receita' && <><br /><strong>O cliente abaixo não é opcional num estorno</strong>: a
                categoria devolve grupo e área, mas o ISS retido (2%) sai do cadastro do cliente. Deixando
                em branco, PIS/COFINS/CSLL/IRPJ voltam e o ISS retido não — sobra dedução num período de
                onde a receita já saiu.</>}
              </Text>}
              style={{ marginBottom: 12 }}
            />
          )}
          <Form.Item label="Descrição" required>
            <Input value={descricao} onChange={e => setDescricao(e.target.value)}
              placeholder="Ex: Receita referente a serviços prestados em 2025" />
          </Form.Item>
          <Form.Item label="Categoria na DRE" required>
            <Select value={categoriaSel || undefined} onChange={(v) => setCategoriaSel(v)}
              placeholder="Selecionar..." showSearch optionFilterProp="label"
              options={cats.map((c: any) => ({ label: c.label, value: c.label }))}
            />
          </Form.Item>

          {tipo === 'receita' && (
            <>
              {/* `required` quando é estorno: sem cliente o ISS retido não volta.
                  Não vira trava — estorno de receita lançada SEM cliente também
                  tem de poder sair sem cliente (o par positivo/negativo é que
                  precisa bater). */}
              <Form.Item label="Cliente" required={estorno}
                extra={estorno
                  ? <Text type="warning" style={{ fontSize: 11 }}>Repita o MESMO cliente do lançamento positivo — é ele que devolve o ISS retido.</Text>
                  : undefined}>
                <Select value={clienteId || undefined} onChange={(v) => setClienteId(v || '')}
                  placeholder={estorno ? 'Mesmo cliente do lançamento positivo' : 'Sem cliente específico'}
                  allowClear showSearch optionFilterProp="label"
                  options={clientes.slice().sort((a, b) => a.razao_social.localeCompare(b.razao_social))
                    .map(c => ({ label: `${c.razao_social}${c.retentor_iss ? ' (retentor ISS)' : ''}`, value: String(c.id) }))}
                />
              </Form.Item>
              <Alert
                type="success" showIcon
                message="Deduções automáticas"
                description={<Text style={{ fontSize: 11 }}>
                  Deduções (PIS 0,65% · COFINS 3% · CSLL 1% · IRPJ 1,5%) e ISS retido (2%, se cliente retentor) são calculados <strong>automaticamente</strong> sobre essa receita. Não precisa inserir manualmente.
                </Text>}
                style={{ marginBottom: 12 }}
              />
            </>
          )}

          <Button type="primary" block onClick={salvar} loading={salvando} icon={<PlusOutlined />}>
            Salvar lançamento
          </Button>
        </Form>
      </Card>

      <Flex align="center" gap={12} wrap="wrap" style={{ marginBottom: 12 }}>
        <Title level={5} style={{ margin: 0, color: '#020D7F' }}>Lançamentos registrados</Title>
        {/* ⚠️ O filtro é por ANO, e sempre foi: o `GET /manuais` monta
            `periodo LIKE 'AAAA/%'` a partir de `trimestre.split('/')[0]`
            (handlers/manuais.js), então os quatro trimestres de um ano
            devolviam exatamente a mesma lista — a tela prometia um recorte
            que o servidor não faz. Virou ano de verdade, e 2025 entrou junto:
            sem ele o estorno de competência de 2025/12 não tinha como ser
            conferido pela tela que deveria conferi-lo. */}
        <Select value={filtroPeriodo} onChange={setFiltroPeriodo} size="small" style={{ width: 92 }}
          options={[
            { label: '2024', value: '2024' },
            { label: '2025', value: '2025' },
            { label: '2026', value: '2026' },
          ]} />
        <Select value={filtroStatus || undefined} onChange={(v) => setFiltroStatus(v || '')}
          placeholder="Todos status" size="small" style={{ width: 130 }} allowClear
          options={[
            { label: 'Pago/Recebido', value: 'pago' },
            { label: 'Em aberto', value: 'em_aberto' },
            { label: 'Cancelado', value: 'cancelado' },
          ]} />
        <Input value={busca} onChange={e => setBusca(e.target.value)}
          placeholder="Buscar descrição/fornecedor/NF…"
          prefix={<SearchOutlined style={{ color: '#aaa' }} />} allowClear size="small"
          style={{ flex: 1, minWidth: 200 }} />
      </Flex>

      {lancamentos.length === 0 ? (
        <Empty description="Nenhum lançamento manual neste período." style={{ padding: 24 }} />
      ) : filtrados.length === 0 ? (
        <Empty description="Nenhum lançamento corresponde aos filtros aplicados." style={{ padding: 24 }} />
      ) : (
        <Space direction="vertical" size={6} style={{ width: '100%' }}>
          {filtrados.map(l => {
            const st = STATUS_TAG[l.status || 'pago'] || STATUS_TAG.pago;
            return (
              <Card key={l.id} size="small" hoverable onClick={() => setEditando(l)} bodyStyle={{ padding: 10 }}>
                <Flex justify="space-between" align="center" gap={12} wrap="wrap">
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Space size={8}>
                      <Text strong style={{ fontSize: 13 }}>{l.descricao}</Text>
                      <Tag color={st.color}>{st.label}</Tag>
                      {ehEstorno(l) && <Tag color="orange">Estorno</Tag>}
                    </Space>
                    <div>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {l.periodo} · {l.categoria_dre}
                        {l.fornecedor_cliente && ` · ${l.fornecedor_cliente}`}
                        {l.nota_fiscal_numero && ` · NF ${l.nota_fiscal_numero}`}
                      </Text>
                    </div>
                    {(l.iss || l.ir || l.pis || l.csll || l.cofins) ? (
                      <Text type="secondary" style={{ fontSize: 10 }}>
                        Impostos: ISS {fmt(l.iss)} · IR {fmt(l.ir)} · PIS {fmt(l.pis)} · CSLL {fmt(l.csll)} · COFINS {fmt(l.cofins)}
                      </Text>
                    ) : null}
                  </div>
                  <Space onClick={e => e.stopPropagation()}>
                    <Text strong style={{ fontSize: 13, color: tipoDoLancamento(l) === 'receita' ? '#0930D0' : '#E24B4A' }}>
                      {dinheiroComSinal(l)}
                    </Text>
                    <Popconfirm title="Excluir este lançamento?" onConfirm={() => excluir(l.id)} okText="Excluir" cancelText="Cancelar">
                      <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                  </Space>
                </Flex>
              </Card>
            );
          })}
        </Space>
      )}

      {editando && (
        <ModalLancamentoManual lancamento={editando}
          categoriasReceita={CATEGORIAS_RECEITA}
          categoriasDespesa={CATEGORIAS_DESPESA}
          clientes={clientes}
          onClose={() => setEditando(null)}
          onMudou={() => { setEditando(null); carregarLancamentos(); }} />
      )}
    </div>
  );
}

function ModalLancamentoManual({ lancamento, categoriasReceita, categoriasDespesa, clientes, onClose, onMudou }: {
  lancamento: Lancamento;
  categoriasReceita: any[];
  categoriasDespesa: any[];
  clientes: Array<{ id: number; razao_social: string; retentor_iss: boolean }>;
  onClose: () => void;
  onMudou: () => void;
}) {
  const { message } = AntApp.useApp();
  const [aba, setAba] = useState<'detalhes' | 'anexos' | 'historico'>('detalhes');
  const [salvando, setSalvando] = useState(false);

  // 🚨 O tipo sai do GRUPO da DRE, nunca do sinal: estorno de competência é
  // receita NEGATIVA, e pela regra antiga (valor >= 0) ele abria aqui como
  // despesa, com o catálogo de despesas no Select — um clique de distância de
  // gravar a receita no grupo errado. Régua e testes em utils/lancamentoManualSinal.
  const ehReceita = tipoDoLancamento(lancamento) === 'receita';
  const cats = ehReceita ? categoriasReceita : categoriasDespesa;

  const [form, setForm] = useState({
    periodo: lancamento.periodo,
    descricao: lancamento.descricao || '',
    categoria_dre: lancamento.categoria_dre || '',
    valor: Math.abs(lancamento.valor || 0),
    // O que liga o lançamento ao ISS retido. Vai no PATCH (handlers/manuais.js
    // já aceita `cliente_id` em `editaveis`) — antes o modal só editava o texto
    // livre `fornecedor_cliente`, que o motor ignora, e um estorno lançado sem
    // cliente só tinha conserto por exclusão e recadastro.
    cliente_id: lancamento.cliente_id ?? null,
    fornecedor_cliente: lancamento.fornecedor_cliente || '',
    cnpj_cpf: lancamento.cnpj_cpf || '',
    data_emissao: (lancamento.data_emissao || '').slice(0, 10),
    data_vencimento: (lancamento.data_vencimento || '').slice(0, 10),
    data_pagamento: (lancamento.data_pagamento || '').slice(0, 10),
    status: lancamento.status || 'pago',
    meio_pagamento: lancamento.meio_pagamento || '',
    nota_fiscal_numero: lancamento.nota_fiscal_numero || '',
    observacoes: lancamento.observacoes || '',
  });

  const [estorno, setEstorno] = useState(ehEstorno(lancamento));

  const set = (k: string, v: any) => setForm(prev => ({ ...prev, [k]: v }));

  async function salvar() {
    // Mesma régua do cadastro: editar o período para fora de 'AAAA/MM' tira o
    // lançamento da DRE sem tirar da lista (LIKE 'AAAA/%' no GET /manuais).
    if (!ehCompetenciaManual(form.periodo)) {
      message.warning('Período precisa ser AAAA/MM — ex: 2025/12.');
      return;
    }
    setSalvando(true);
    try {
      const token = await getToken();
      const valor = valorParaGravar(form.valor, ehReceita ? 'receita' : 'despesa', estorno);
      const cat = cats.find((c: any) => c.label === form.categoria_dre) as any;
      const payload = {
        ...form, valor,
        grupo_dre: cat?.grupo || lancamento.grupo_dre,
        area_negocio: cat?.area || lancamento.area_negocio || '',
        data_emissao: form.data_emissao || null,
        data_vencimento: form.data_vencimento || null,
        data_pagamento: form.data_pagamento || null,
      };
      await axios.request({
        url: `${API_URL}/manuais/${lancamento.id}`,
        method: 'PATCH', data: payload,
        headers: { Authorization: token },
      });
      message.success('Alterações salvas.');
      onMudou();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.erro || e.message));
    } finally { setSalvando(false); }
  }

  const st = STATUS_TAG[form.status] || STATUS_TAG.pago;

  return (
    <Modal
      open onCancel={onClose} width={780} destroyOnClose
      footer={aba === 'detalhes' ? [
        <Button key="c" onClick={onClose}>Fechar</Button>,
        <Button key="s" type="primary" loading={salvando} onClick={salvar}>Salvar alterações</Button>,
      ] : null}
      title={
        <Space direction="vertical" size={2}>
          <Text type="secondary" style={{ fontSize: 10 }}>
            {lancamento.periodo} · {ehReceita ? 'Receita' : 'Despesa'} ·
            {' '}<strong>{dinheiroComSinal(lancamento)}</strong>
          </Text>
          <Space>
            <Text strong style={{ color: '#020D7F' }}>{lancamento.descricao}</Text>
            <Tag color={st.color}>{st.label}</Tag>
            {ehEstorno(lancamento) && <Tag color="orange">Estorno</Tag>}
          </Space>
        </Space>
      }
    >
      <Tabs
        activeKey={aba} onChange={(k) => setAba(k as any)} size="small"
        items={[
          {
            key: 'detalhes', label: 'Detalhes',
            children: (
              <Form layout="vertical" size="small">
                <Row gutter={10}>
                  <Col span={8}>
                    <Form.Item label="Período (AAAA/MM)"
                      validateStatus={form.periodo && !ehCompetenciaManual(form.periodo) ? 'error' : undefined}
                      help={form.periodo && !ehCompetenciaManual(form.periodo)
                        ? 'A DRE só enxerga AAAA/MM.' : undefined}>
                      <Input value={form.periodo} onChange={e => set('periodo', e.target.value)}
                        onBlur={e => set('periodo', normalizarCompetencia(e.target.value))} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label="Valor (R$)">
                      <InputNumber {...PROPS_DINHEIRO_BR} value={form.valor} onChange={(v) => set('valor', Number(v) || 0)}
                        min={0} step={0.01} style={{ width: '100%' }} />
                      <Checkbox checked={estorno} onChange={e => setEstorno(e.target.checked)} style={{ marginTop: 6 }}>
                        <Text style={{ fontSize: 12 }}>É um estorno</Text>
                      </Checkbox>
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label="Status">
                      <Select value={form.status} onChange={(v) => set('status', v)}
                        options={[
                          { label: ehReceita ? 'Recebido' : 'Pago', value: 'pago' },
                          { label: 'Em aberto', value: 'em_aberto' },
                          { label: 'Cancelado', value: 'cancelado' },
                        ]} />
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item label="Descrição">
                  <Input value={form.descricao} onChange={e => set('descricao', e.target.value)} />
                </Form.Item>
                <Form.Item label="Categoria DRE">
                  <Select value={form.categoria_dre || undefined} onChange={(v) => set('categoria_dre', v)}
                    showSearch optionFilterProp="label" placeholder="Selecionar…"
                    options={cats.map((c: any) => ({ label: c.label, value: c.label }))} />
                </Form.Item>
                {ehReceita && (
                  <Form.Item label="Cliente (cadastro)" required={estorno}
                    extra={<Text type="secondary" style={{ fontSize: 11 }}>
                      É este vínculo — não o texto abaixo — que define o ISS retido de 2%.
                      {estorno && ' Num estorno tem de ser o MESMO cliente do lançamento positivo, senão o ISS retido não volta.'}
                    </Text>}>
                    <Select value={form.cliente_id != null ? String(form.cliente_id) : undefined}
                      onChange={(v) => set('cliente_id', v ? parseInt(v) : null)}
                      placeholder="Sem cliente específico" allowClear showSearch optionFilterProp="label"
                      options={clientes.slice().sort((a, b) => a.razao_social.localeCompare(b.razao_social))
                        .map(c => ({ label: `${c.razao_social}${c.retentor_iss ? ' (retentor ISS)' : ''}`, value: String(c.id) }))}
                    />
                  </Form.Item>
                )}
                <Row gutter={10}>
                  <Col span={12}>
                    <Form.Item label={ehReceita ? 'Cliente (texto livre)' : 'Fornecedor'}>
                      <Input value={form.fornecedor_cliente} onChange={e => set('fornecedor_cliente', e.target.value)}
                        placeholder={ehReceita ? 'Razão social do cliente' : 'Razão social do fornecedor'} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="CNPJ/CPF">
                      <Input value={form.cnpj_cpf} onChange={e => set('cnpj_cpf', e.target.value)}
                        placeholder="00.000.000/0000-00" />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={10}>
                  <Col span={8}>
                    <Form.Item label="Data emissão">
                      <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY"
                        value={form.data_emissao ? dayjs(form.data_emissao) : null}
                        onChange={(d) => set('data_emissao', d ? d.format('YYYY-MM-DD') : '')} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label="Data vencimento">
                      <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY"
                        value={form.data_vencimento ? dayjs(form.data_vencimento) : null}
                        onChange={(d) => set('data_vencimento', d ? d.format('YYYY-MM-DD') : '')} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label={ehReceita ? 'Data recebimento' : 'Data pagamento'}>
                      <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY"
                        value={form.data_pagamento ? dayjs(form.data_pagamento) : null}
                        onChange={(d) => set('data_pagamento', d ? d.format('YYYY-MM-DD') : '')} />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={10}>
                  <Col span={12}>
                    <Form.Item label="Nota fiscal nº">
                      <Input value={form.nota_fiscal_numero} onChange={e => set('nota_fiscal_numero', e.target.value)} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="Meio de pagamento">
                      <Select value={form.meio_pagamento || undefined} onChange={(v) => set('meio_pagamento', v || '')}
                        allowClear placeholder="—"
                        options={[
                          { label: 'PIX', value: 'pix' }, { label: 'Boleto', value: 'boleto' },
                          { label: 'TED', value: 'ted' }, { label: 'Depósito', value: 'deposito' },
                          { label: 'Cheque', value: 'cheque' }, { label: 'Cartão', value: 'cartao' },
                          { label: 'Dinheiro', value: 'dinheiro' }, { label: 'Outro', value: 'outro' },
                        ]} />
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item label="Observações">
                  <Input.TextArea value={form.observacoes} onChange={e => set('observacoes', e.target.value)} rows={3} />
                </Form.Item>
              </Form>
            ),
          },
          {
            key: 'anexos',
            label: <span><FileTextOutlined /> Anexos</span>,
            children: <AnexosLancamento lancamentoId={lancamento.id} />,
          },
          {
            key: 'historico',
            label: <span><HistoryOutlined /> Histórico</span>,
            children: <HistoricoLancamento lancamentoId={lancamento.id} />,
          },
        ]}
      />
    </Modal>
  );
}

function AnexosLancamento({ lancamentoId }: { lancamentoId: string }) {
  const { message, modal } = AntApp.useApp();
  const [anexos, setAnexos] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [tipoUp, setTipoUp] = useState('nf');

  async function carregar() {
    setLoading(true);
    try {
      const token = await getToken();
      const r = await axios.get(`${API_URL}/manuais/${lancamentoId}/anexos`,
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setAnexos(Array.isArray(d) ? d : []);
    } finally { setLoading(false); }
  }
  useEffect(() => { carregar(); /* eslint-disable-next-line */ }, [lancamentoId]);

  async function upload(file: File) {
    setUploading(true);
    try {
      const token = await getToken();
      const r1 = await axios.post(`${API_URL}/manuais/${lancamentoId}/anexos/upload-url`,
        { filename: file.name, content_type: file.type },
        { headers: { Authorization: token } });
      const d1 = r1.data.body ? JSON.parse(r1.data.body) : r1.data;
      await fetch(d1.upload_url, { method: 'PUT', body: file,
        headers: { 'Content-Type': file.type || 'application/octet-stream' } });
      await axios.post(`${API_URL}/manuais/${lancamentoId}/anexos`,
        { nome: file.name, s3_key: d1.s3_key, tipo: tipoUp,
          tamanho_bytes: file.size, mime: file.type },
        { headers: { Authorization: token } });
      message.success('Anexo enviado.');
      await carregar();
    } catch (e: any) {
      message.error('Erro upload: ' + (e.response?.data?.erro || e.message));
    } finally { setUploading(false); }
  }

  async function baixar(a: any) {
    const token = await getToken();
    const r = await axios.get(`${API_URL}/manuais/anexos/${a.id}/download`,
      { headers: { Authorization: token } });
    const d = r.data.body ? JSON.parse(r.data.body) : r.data;
    window.open(d.download_url, '_blank');
  }

  function remover(a: any) {
    modal.confirm({
      title: `Remover anexo "${a.nome}"?`, okText: 'Remover', cancelText: 'Cancelar', okButtonProps: { danger: true },
      onOk: async () => {
        const token = await getToken();
        await axios.delete(`${API_URL}/manuais/anexos/${a.id}`, { headers: { Authorization: token } });
        await carregar();
      },
    });
  }

  const fmtTam = (b: number | null) => {
    if (!b) return '—';
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / 1024 / 1024).toFixed(1)} MB`;
  };

  return (
    <div>
      <Card size="small" style={{ marginBottom: 14, background: '#FAFBFF', borderStyle: 'dashed' }} bodyStyle={{ padding: 10 }}>
        <Flex gap={8} align="center" wrap="wrap">
          <Select value={tipoUp} onChange={setTipoUp} size="small" style={{ width: 180 }}
            options={[
              { label: '🧾 Nota Fiscal', value: 'nf' },
              { label: '💸 Comprovante pagamento', value: 'comprovante' },
              { label: '📋 Boleto', value: 'boleto' },
              { label: '📄 Contrato', value: 'contrato' },
              { label: '📎 Outro', value: 'outro' },
            ]} />
          <Upload
            customRequest={({ file, onSuccess }) => {
              upload(file as File).then(() => onSuccess?.({}));
            }}
            showUploadList={false}
            disabled={uploading}
            accept=".pdf,.jpg,.jpeg,.png,.xml,.xlsx,.docx"
          >
            <Button type="primary" size="small" icon={<UploadOutlined />} loading={uploading}>
              Anexar arquivo
            </Button>
          </Upload>
        </Flex>
      </Card>
      {loading ? <Card loading /> : anexos.length === 0 ? (
        <Empty description="Nenhum anexo. Suba a NF aqui pra ter tudo num lugar só." />
      ) : (
        <Space direction="vertical" size={6} style={{ width: '100%' }}>
          {anexos.map(a => (
            <Card key={a.id} size="small" bodyStyle={{ padding: 8 }}>
              <Flex justify="space-between" align="center" gap={8}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text strong style={{ fontSize: 12 }} ellipsis>{a.nome}</Text>
                  <div>
                    <Text type="secondary" style={{ fontSize: 10 }}>
                      {a.tipo} · {fmtTam(a.tamanho_bytes)} · {new Date(a.criado_em).toLocaleDateString('pt-BR')}
                      {a.criado_por && ` · ${a.criado_por.split('@')[0]}`}
                    </Text>
                  </div>
                </div>
                <Space size={4}>
                  <Button size="small" icon={<DownloadOutlined />} onClick={() => baixar(a)}>Baixar</Button>
                  <Button size="small" danger icon={<DeleteOutlined />} onClick={() => remover(a)} />
                </Space>
              </Flex>
            </Card>
          ))}
        </Space>
      )}
    </div>
  );
}

function HistoricoLancamento({ lancamentoId }: { lancamentoId: string }) {
  const [eventos, setEventos] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      const token = await getToken();
      const r = await axios.get(`${API_URL}/manuais/${lancamentoId}/eventos`,
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setEventos(Array.isArray(d) ? d : []);
      setLoading(false);
    })();
  }, [lancamentoId]);
  if (loading) return <Card loading />;
  if (eventos.length === 0) return <Empty description="Sem histórico ainda. Mudanças aparecem aqui." />;
  const cor: Record<string, string> = {
    criado: '#888', status: '#0930D0', editado: '#B07500',
    anexo_add: '#0930D0', anexo_del: '#888',
  };
  return (
    <Space direction="vertical" size={8} style={{ width: '100%' }}>
      {eventos.map(ev => (
        <Card key={ev.id} size="small" bodyStyle={{ padding: 10 }}
          style={{ background: '#FAFBFF', borderLeft: `3px solid ${cor[ev.tipo] || '#888'}` }}>
          <Text strong style={{ fontSize: 11 }}>
            <Tag color="default" style={{ marginRight: 6 }}>{ev.tipo}</Tag>
            {ev.descricao || '—'}
          </Text>
          <div>
            <Text type="secondary" style={{ fontSize: 10 }}>
              {new Date(ev.criado_em).toLocaleString('pt-BR')}
              {ev.criado_por && ` · ${String(ev.criado_por).split('@')[0]}`}
            </Text>
          </div>
        </Card>
      ))}
    </Space>
  );
}

export default function LancamentosManuais() {
  return (
    <AntApp>
      <LancamentosManuaisInner />
    </AntApp>
  );
}
